import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SEO from '../components/SEO/SEO';

const DownloadPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') === 'client' ? 'client' : 'sr';
  const [activeTab, setActiveTab] = useState(initialTab);

  const switchTab = (tab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };
  const [testimonials, setTestimonials] = useState([]);
  const [isLoadingTestimonials, setIsLoadingTestimonials] = useState(true);
  const [seminars, setSeminars] = useState([]);
  const [isLoadingSeminars, setIsLoadingSeminars] = useState(true);
  const [downloads, setDownloads] = useState([]);

  // ダウンロード資料をJSONから取得
  useEffect(() => {
    fetch('/data/downloads.json?t=' + Date.now())
      .then((r) => r.ok ? r.json() : [])
      .then(setDownloads)
      .catch(() => setDownloads([]));
  }, []);

  // お客様の声をJSONから取得
  useEffect(() => {
    const fetchTestimonials = async () => {
      setIsLoadingTestimonials(true);
      try {
        const res = await fetch('/data/testimonials.json?t=' + Date.now());
        const data = await res.json();
        const category = activeTab === 'client' ? 'エンドユーザー' : '社労士';
        const filtered = Array.isArray(data)
          ? data.filter(item => Array.isArray(item.category) && item.category.includes(category))
          : [];
        setTestimonials(filtered);
      } catch (error) {
        console.error('お客様の声の取得に失敗しました:', error);
        setTestimonials([]);
      } finally {
        setIsLoadingTestimonials(false);
      }
    };
    fetchTestimonials();
  }, [activeTab]);

  // セミナー情報をJSONから取得
  useEffect(() => {
    const fetchSeminars = async () => {
      setIsLoadingSeminars(true);
      try {
        const res = await fetch('/data/seminars.json?t=' + Date.now());
        const data = await res.json();
        const today = new Date().toISOString().split('T')[0];
        const filtered = (Array.isArray(data) ? data : []).filter((s) => !s.eventDate || s.eventDate >= today);
        setSeminars(filtered);
      } catch (error) {
        console.error('セミナー情報の取得に失敗しました:', error);
        setSeminars([]);
      } finally {
        setIsLoadingSeminars(false);
      }
    };
    fetchSeminars();
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="お客様の声・資料ダウンロード"
        description="SrWithの導入事例やお客様の声をご紹介。サービスパンフレットの無料ダウンロード、社労士向けセミナー情報もこちらからご確認いただけます。"
      />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-pink-beige to-white">
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full -translate-y-48 translate-x-48 blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-white/5 rounded-full translate-y-40 -translate-x-40 blur-2xl"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-20 md:py-32">
          <div className="text-center space-y-8">
            <h1 className="text-4xl md:text-6xl font-black text-brown leading-tight">
              お客様の声・資料請求
            </h1>
            <p className="text-xl md:text-2xl text-gray-text max-w-3xl mx-auto leading-relaxed">
              <span className="font-semibold text-white px-2 py-1 rounded" style={{backgroundColor: '#D8C5C9'}}>SrWith</span>のサービス詳細や導入事例など、<br />
              各種資料を無料でダウンロードいただけます
            </p>
          </div>

          {/* タブ切り替えボタン */}
          <div className="flex justify-center space-x-4 mt-12">
            <button
              onClick={() => switchTab('sr')}
              className={`px-8 py-4 rounded-2xl font-semibold text-lg transition-all duration-300 ${
                activeTab === 'sr'
                  ? 'bg-rose-100 text-brown shadow-xl scale-105 border-2 border-rose-300'
                  : 'bg-white/30 text-brown hover:bg-white/50 hover:shadow-lg border-2 border-gray-200'
              }`}
            >
              社労士の方
            </button>
            <button
              onClick={() => switchTab('client')}
              className={`px-8 py-4 rounded-2xl font-semibold text-lg transition-all duration-300 ${
                activeTab === 'client'
                  ? 'bg-rose-100 text-brown shadow-xl scale-105 border-2 border-rose-300'
                  : 'bg-white/30 text-brown hover:bg-white/50 hover:shadow-lg border-2 border-gray-200'
              }`}
            >
              企業・事業主の方
            </button>
          </div>
        </div>
      </section>

      {/* お客様の声セクション */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-brown mb-6">
              お客様の声
            </h2>
            <p className="text-lg md:text-xl text-gray-text">
              {activeTab === 'client'
                ? (<><span className="font-semibold text-white px-2 py-1 rounded" style={{backgroundColor: '#D8C5C9'}}>SrWith</span>を導入いただいた企業様からの声をご紹介します</>)
                : (<><span className="font-semibold text-white px-2 py-1 rounded" style={{backgroundColor: '#D8C5C9'}}>SrWith</span>パートナーとして活動されている社労士の先生方の声をご紹介します</>)
              }
            </p>
          </div>

          {isLoadingTestimonials ? (
            <div className="text-center py-12">
              <p className="text-gray-500">読み込み中...</p>
            </div>
          ) : testimonials.length > 0 ? (
            <div className="space-y-8">
              {testimonials.map((testimonial) => (
                <div
                  key={testimonial.id}
                  className="bg-gradient-to-br from-pink-beige/5 to-white border-2 border-pink-beige/20 rounded-2xl p-8 md:p-12 shadow-lg text-left"
                >
                  {testimonial.image && testimonial.image.url && (
                    <div className="mb-6">
                      <img
                        src={testimonial.image.url}
                        alt={testimonial.title}
                        className="w-full max-w-[224px] h-auto rounded-lg shadow-md"
                      />
                    </div>
                  )}
                  <h3 className="text-2xl font-bold text-brown mb-4">
                    {testimonial.title}
                  </h3>
                  {(testimonial.companyType || testimonial.employeeCount) && (
                    <div className="flex items-center space-x-4 mb-6 text-sm text-gray-600">
                      {testimonial.companyType && (
                        <span className="bg-pink-beige/10 px-3 py-1 rounded-full">
                          業種：{testimonial.companyType}
                        </span>
                      )}
                      {testimonial.employeeCount && (
                        <span className="bg-pink-beige/10 px-3 py-1 rounded-full">
                          従業員数：{testimonial.employeeCount}
                        </span>
                      )}
                    </div>
                  )}
                  <div
                    className="text-gray-text leading-relaxed prose prose-lg max-w-none"
                    dangerouslySetInnerHTML={{ __html: testimonial.content }}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500">お客様の声はまだ登録されていません。</p>
            </div>
          )}
        </div>
      </section>

      {/* 資料ダウンロードセクション */}
      <section className="py-20 md:py-32 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          {(() => {
            const doc = downloads.find((d) => d.tab === activeTab);
            if (!doc || !doc.fileUrl) return (
              <div className="text-center bg-white p-8 md:p-12 rounded-2xl shadow-xl">
                <p className="text-gray-500">資料は準備中です。</p>
              </div>
            );
            return (
              <div className="text-center bg-white p-8 md:p-12 rounded-2xl shadow-xl">
                <h2 className="text-3xl md:text-4xl font-bold text-brown mb-6">
                  {activeTab === 'client'
                    ? (<><span className="font-semibold text-white px-2 py-1 rounded" style={{backgroundColor: '#D8C5C9'}}>SrWith</span> パンフレット</>)
                    : '社労士向け資料をダウンロード'
                  }
                </h2>
                <p className="text-lg md:text-xl text-gray-text mb-8 max-w-2xl mx-auto">
                  {activeTab === 'client'
                    ? (<><span className="font-semibold text-white px-2 py-1 rounded" style={{backgroundColor: '#D8C5C9'}}>SrWith</span>のサービス概要や導入メリットをまとめたパンフレットをダウンロードいただけます</>)
                    : 'パートナー事務所向けの資料をダウンロードいただけます。'
                  }
                </p>
                <a
                  href={doc.fileUrl}
                  download
                  className="inline-block bg-pink-beige text-white px-12 py-4 rounded-2xl text-lg font-semibold hover:bg-brown transition-all duration-300 transform hover:scale-105 shadow-lg"
                >
                  {activeTab === 'client' ? '企業向け資料をダウンロード' : '社労士向け資料をダウンロード'}
                </a>
              </div>
            );
          })()}
        </div>
      </section>

      {/* セミナー情報セクション（社労士タブのみ） */}
      {activeTab === 'sr' && (
        <section className="py-20 md:py-32 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-brown mb-6">
                セミナー情報
              </h2>
              <p className="text-lg md:text-xl text-gray-text">
                社労士向けのセミナー・勉強会の情報をご案内します
              </p>
            </div>

            {isLoadingSeminars ? (
              <div className="text-center py-12">
                <p className="text-gray-500">読み込み中...</p>
              </div>
            ) : seminars.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {seminars.map((seminar) => (
                  <div
                    key={seminar.id}
                    className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden"
                  >
                    <div className="p-6">
                      {seminar.badge && (
                        <span className="inline-block text-xs font-bold text-white bg-pink-beige px-3 py-1 rounded-full mb-3">
                          {seminar.badge}
                        </span>
                      )}
                      <div className="text-sm text-pink-beige font-semibold mb-2">
                        {seminar.fullDate || `${seminar.date} ${seminar.time}`}
                      </div>
                      <h3 className="text-xl font-bold text-brown mb-4">
                        {seminar.fullTitle || seminar.shortTitle}
                      </h3>
                      {seminar.intro && (
                        <p className="text-gray-text text-sm mb-4 leading-relaxed">
                          {seminar.intro}
                        </p>
                      )}
                      {seminar.bullets && seminar.bullets.length > 0 && (
                        <ul className="space-y-1 mb-4">
                          {seminar.bullets.map((b, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-gray-text">
                              <span className="text-pink-beige mt-0.5 shrink-0">●</span>
                              <span>{b}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                      <Link
                        to="/lp/academy/seminar"
                        className="block w-full bg-pink-beige text-white text-center py-3 rounded-xl font-semibold hover:bg-brown transition-all duration-300 transform hover:scale-105"
                      >
                        詳細・申込
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-500">現在開催予定のセミナーはありません。</p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-pink-beige">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-brown mb-8">
            {activeTab === 'client'
              ? 'さらに詳しく知りたい方へ'
              : 'パートナー登録をご検討の方へ'
            }
          </h2>
          <p className="text-lg md:text-xl text-brown mb-12 max-w-2xl mx-auto leading-relaxed">
            {activeTab === 'client'
              ? '資料をご覧いただき、ご不明点やご質問がございましたら、お気軽にお問い合わせください。'
              : 'まずは情報交換から始めさせていただきます。同業者として、業界の未来について一緒に考えませんか？'
            }
          </p>

          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link
              to="/contact"
              className="inline-block bg-brown text-white px-10 py-4 rounded-2xl text-lg font-semibold hover:bg-brown/90 transition-all duration-300 transform hover:scale-105 shadow-xl"
            >
              お問い合わせ
            </Link>
            {activeTab === 'client' && (
              <Link
                to="/service?tab=company"
                className="inline-block bg-white text-brown px-10 py-4 rounded-2xl text-lg font-semibold hover:bg-gray-50 transition-all duration-300 transform hover:scale-105 shadow-xl border-2 border-brown"
              >
                サービス詳細を見る
              </Link>
            )}
            {activeTab === 'sr' && (
              <Link
                to="/partner"
                className="inline-block bg-white text-brown px-10 py-4 rounded-2xl text-lg font-semibold hover:bg-gray-50 transition-all duration-300 transform hover:scale-105 shadow-xl border-2 border-brown"
              >
                パートナー事務所一覧
              </Link>
            )}
          </div>
        </div>
      </section>

    </div>
  );
};

export default DownloadPage;
